import React, { useState, useEffect, useRef } from 'react';
import Popup from 'reactjs-popup';
import 'reactjs-popup/dist/index.css';
import { Button, Container } from 'react-bootstrap';
import { XLg } from 'react-bootstrap-icons';
import { TEXT_SECONDARY } from '../../../../../../../../Utils/ColorsConfiguration';
import { sanitizeHTML } from '../../../../../../../../Utils/Sanitize';
import styles from '../../GCPProductDetail.module.scss';

/**
 * GCPTnCPopup
 * -----------
 * Displays Synnex's Google Cloud Terms of Program.
 *
 * The TnC content is fetched by the PARENT when the Order Confirmation popup
 * opens (provision flow) or on page load (management console) — so the
 * content is pre-loaded by the time the user clicks the "Google Cloud Terms
 * of Program" link.
 *
 * Behaviour:
 *  - readOnly=false (default): scroll gate is active.
 *      "Accept & Close" is DISABLED until the user scrolls to the bottom.
 *      A yellow warning banner is shown in a fixed-height area.
 *  - readOnly=true (API already accepted): scroll gate is bypassed.
 *      Only a "Close" button is shown — user can re-read but not re-accept.
 *      The amber warning banner is hidden.
 *  - If content is short enough that no scroll bar appears (scrollHeight <=
 *    clientHeight) and readOnly=false: "Accept & Close" is enabled immediately.
 *  - On "Accept & Close": calls onAccept() — parent handles state update.
 *
 * Props:
 *  @param {boolean}  open        - Whether the popup is open.
 *  @param {function} onClose     - Called when popup is closed without accepting.
 *  @param {function} onAccept    - Called when user clicks "Accept & Close".
 *  @param {string}   tncContent  - HTML string of TnC content (pre-fetched by parent).
 *  @param {boolean}  tncLoading  - True while the parent is fetching TnC data.
 *  @param {boolean}  readOnly    - When true: bypass scroll gate, show Close only.
 */
const GCPTnCPopup = ({ open, onClose, onAccept, tncContent, tncLoading, readOnly }) => {
    const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
    const scrollContainerRef = useRef(null);

    // ----------------------------------------------------------------
    // On popup open: reset scroll state, scroll back to top, then check
    // whether the content even requires scrolling.
    // If readOnly=true (API already accepted), bypass scroll gate entirely.
    // ----------------------------------------------------------------
    useEffect(() => {
        if (open) {
            if (readOnly) {
                // Already accepted — no scroll gate needed.
                setHasScrolledToBottom(true);
            } else {
                setHasScrolledToBottom(false);
            }
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTop = 0;
            }
        }
    }, [open, readOnly]);

    // After content is rendered (tncLoading → false, tncContent available),
    // check whether scrolling is actually needed. If the full content fits
    // within the visible container, auto-enable Accept & Close.
    // Skip this check when readOnly (already bypassed above).
    useEffect(() => {
        if (open && !readOnly && !tncLoading && tncContent && scrollContainerRef.current) {
            const { scrollHeight, clientHeight } = scrollContainerRef.current;
            if (scrollHeight <= clientHeight) {
                // Content fits without scrolling — enable button immediately.
                setHasScrolledToBottom(true);
            }
        }
    }, [open, readOnly, tncLoading, tncContent]);

    // ----------------------------------------------------------------
    // Scroll detection — enable "Accept & Close" when bottom is reached
    // ----------------------------------------------------------------
    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        // 5px tolerance for sub-pixel rounding
        if (scrollHeight - scrollTop - clientHeight < 5) {
            setHasScrolledToBottom(true);
        }
    };

    return (
        <Popup
            open={open}
            onClose={onClose}
            className="custom-modal custom-modal-xxl"
            closeOnDocumentClick={false}
            closeOnEscape={false}
            modal
            nested
        >
            {(close) => (
                <div className="modal">
                    {/* ---- Header ---- */}
                    <div className="header">
                        <h2>Synnex's Google Cloud Terms of Program</h2>
                        <XLg
                            size={24}
                            className="cpointer close-icon-popup"
                            onClick={() => {
                                if (onClose) onClose();
                                close();
                            }}
                            color={TEXT_SECONDARY}
                        />
                    </div>

                    {/* ---- Scrollable TnC Content ---- */}
                    <Container fluid className="p-0">
                        <div
                            ref={scrollContainerRef}
                            className={styles['gcp-tnc-scroll-container']}
                            onScroll={handleScroll}
                        >
                            {tncLoading && (
                                <p className="text-center py-5">Loading terms…</p>
                            )}
                            {!tncLoading && tncContent && (
                                <div
                                    className="px-4 py-3"
                                    dangerouslySetInnerHTML={sanitizeHTML(tncContent)}
                                />
                            )}
                            {!tncLoading && !tncContent && (
                                <p className="text-center py-5 text-muted">
                                    Unable to load terms. Please try again later.
                                </p>
                            )}
                        </div>

                        {/*
                            Fixed-height warning area — always rendered to prevent layout shift.
                            Hidden entirely when readOnly (no scroll requirement).
                            Shows yellow warning when scroll needed; plain white placeholder once done.
                        */}
                        {!readOnly && (
                            <div className={!hasScrolledToBottom ? styles['gcp-tnc-scroll-warning'] : styles['gcp-tnc-scroll-warning-empty']}>
                                {!hasScrolledToBottom && (
                                    <>
                                        <span className={styles['gcp-tnc-warning-icon']}>!</span>
                                        &nbsp;Please scroll to the bottom to read the full terms before accepting
                                    </>
                                )}
                            </div>
                        )}
                        {readOnly && (
                            <div className={styles['gcp-tnc-scroll-warning-empty']}></div>
                        )}

                        {/* ---- Footer ---- */}
                        {/* readOnly: show only Close button (user can re-read but not re-accept) */}
                        {/* Non-readOnly: show Accept & Close (disabled until scrolled to bottom) */}
                        <div className={(!readOnly && !hasScrolledToBottom) ? styles['gcp-tnc-footer-bordered'] : styles['gcp-tnc-footer']}>
                            {readOnly ? (
                                <Button
                                    className="px-4"
                                    onClick={() => {
                                        if (onClose) onClose();
                                        close();
                                    }}
                                    variant="primary"
                                >
                                    Close
                                </Button>
                            ) : (
                                <Button
                                    className="px-4"
                                    disabled={!hasScrolledToBottom}
                                    onClick={() => { if (onAccept) onAccept(); }}
                                    variant={hasScrolledToBottom ? 'primary' : 'light'}
                                >
                                    Accept & Close
                                </Button>
                            )}
                        </div>
                    </Container>
                </div>
            )}
        </Popup>
    );
};

export default GCPTnCPopup;
